/**
 * Long-form variant of in-app notification popup. Renders the broadcast
 * through the shared <LongFormModal/> — the same visual language as the
 * pricing-page FreeGensInfo modal.
 *
 * Field mapping:
 *   notification.title       → LongFormModal title
 *   notification.description → <Lead/> paragraph
 *   templateData.sections    → <Section/> + <Para/> + <List/>
 *   notification.actions     → footer row of buttons
 */

import { useMemo } from 'react';
import {
  LongFormModal,
  Lead,
  Section,
  Para,
  List,
} from '../LongFormModal';
import { MediaGallery } from '../MediaGallery';
import type { ShowcasePhoto } from '../ShowcaseModal';
import type {
  InAppNotification,
  LongFormTemplateData,
} from '../../../types';
import { pickLocalized } from '../../../utils/localized';
import {
  useNotificationActionHandler,
  useNotificationLanguage,
  resolveActionLabel,
  actionEmoji,
  parseMediaUrls,
} from './useActionHandler';
import styles from './LongForm.module.css';

interface LongFormProps {
  notification: InAppNotification;
  onDismiss: () => void;
}

const LongForm = ({ notification, onDismiss }: LongFormProps) => {
  const handleAction = useNotificationActionHandler(onDismiss);
  const lang = useNotificationLanguage();

  const data = (notification.templateData ?? { sections: [] }) as LongFormTemplateData;
  const actions = notification.actions ?? [];

  // Resolve media — same chain as Showcase: localizedMedia[lang] →
  // localizedMedia.ru → legacy imageUrls/videoUrls + captions.
  const photos = useMemo<ShowcasePhoto[]>(() => {
    const lm = notification.localizedMedia;
    const localizedSet = lm?.[lang] || lm?.ru;
    let images: string[];
    let videos: string[];
    let captions: (string | undefined)[];
    if (localizedSet) {
      images = localizedSet.imageUrls || [];
      videos = localizedSet.videoUrls || [];
      captions = localizedSet.captions || [];
    } else {
      images = parseMediaUrls(notification.imageUrl, notification.imageUrls);
      videos = parseMediaUrls(notification.videoUrl, notification.videoUrls);
      const legacy = notification.captions || [];
      captions = legacy.map((c) => pickLocalized(c, lang));
    }
    const result: ShowcasePhoto[] = [];
    images.forEach((url, i) => result.push({ url, type: 'image', caption: captions[i] }));
    videos.forEach((url, i) => result.push({ url, type: 'video', caption: captions[images.length + i] }));
    return result;
  }, [
    notification.imageUrl,
    notification.imageUrls,
    notification.videoUrl,
    notification.videoUrls,
    notification.captions,
    notification.localizedMedia,
    lang,
  ]);

  const footer = actions.length > 0 ? (
    <div className={styles.footer}>
      {actions.map((action, i) => (
        <button
          key={i}
          type="button"
          className={styles.footerButton}
          onClick={() => handleAction(action)}
        >
          {actionEmoji(action.type)}
          {resolveActionLabel(action, lang)}
        </button>
      ))}
    </div>
  ) : undefined;

  // Admin-authored content may contain inline HTML — match the legacy Simple
  // popup which used dangerouslySetInnerHTML. Localized fields are resolved
  // to the user's current language first.
  const html = (s: string) => <span dangerouslySetInnerHTML={{ __html: s }} />;
  const titleStr = pickLocalized(notification.title, lang);
  const descStr = pickLocalized(notification.description, lang);

  return (
    <LongFormModal
      title={titleStr}
      onClose={onDismiss}
      footer={footer}
    >
      {photos.length > 0 && (
        <div className={styles.mediaWrap}>
          <MediaGallery photos={photos} ariaLabelFallback={titleStr} />
        </div>
      )}

      {descStr && <Lead>{html(descStr)}</Lead>}

      {data.sections?.map((section, i) => (
        <Section key={i} title={pickLocalized(section.title, lang)}>
          {section.paragraphs?.map((para, j) => (
            <Para key={j}>{html(pickLocalized(para, lang))}</Para>
          ))}
          {section.bullets && section.bullets.length > 0 && (
            <List items={section.bullets.map((b) => html(pickLocalized(b, lang)))} />
          )}
        </Section>
      ))}
    </LongFormModal>
  );
};

export default LongForm;
